import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Typography,
  Space,
  Row,
  Col,
  Alert,
  Divider
} from 'antd';
import { TeamOutlined, UserOutlined, MailOutlined, PhoneOutlined, IdcardOutlined } from '@ant-design/icons';
import { Consultor, ConsultorFormData } from '@/features/consultores/types/consultor';
import { ConsultorProgressSteps } from './ConsultorProgressSteps';
import { ConsultorFormHeader } from './ConsultorFormHeader';

const { Text, Title } = Typography;

// Schema de validação para o formulário
const consultorFormSchema = z.object({
  nome: z.string().min(1, "Nome é obrigatório"),
  codigo: z.string().optional(),
  email: z.string().optional().refine((val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), {
    message: "Email inválido"
  }).or(z.literal('')),
  cpf: z.string().optional(),
  telefone: z.string().optional(),
  tipo: z.enum(['supervisor', 'lider', 'relacionador', 'master'] as const),
  supervisor_id: z.coerce.number().nullable().optional(),
  status: z.enum(["ativo", "inativo"]).optional(),
  tenant_id: z.coerce.number().optional(),
});

interface ConsultorStepFormProps {
  consultor?: (Consultor & { telefone?: string }) | null;
  tenants: { id: number; nome: string }[];
  supervisores: Consultor[];
  isSuperAdmin: boolean;
  defaultTenantId?: string;
  onSubmit: (data: ConsultorFormData) => Promise<void>;
  onCancel: () => void;
}

export function ConsultorStepForm({
  consultor,
  tenants,
  supervisores,
  isSuperAdmin,
  defaultTenantId,
  onSubmit,
  onCancel,
}: ConsultorStepFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const cleanupPlaceholder = (val?: string) => {
    if (!val || val === 'não informado' || val === '-') return '';
    return val;
  };

  const { control, handleSubmit, watch, setValue, setError, clearErrors, formState: { errors } } = useForm<ConsultorFormData>({
    resolver: zodResolver(consultorFormSchema),
    defaultValues: {
      nome: consultor?.nome || '',
      codigo: cleanupPlaceholder(consultor?.codigo),
      email: cleanupPlaceholder(consultor?.email),
      cpf: consultor?.cpf || '',
      telefone: cleanupPlaceholder(consultor?.telefone),
      tipo: consultor?.tipo || 'relacionador',
      supervisor_id: consultor?.supervisor_id || null,
      status: consultor?.status || 'ativo',
      tenant_id: consultor?.tenant_id || (defaultTenantId ? Number(defaultTenantId) : undefined),
    },
  });

  const tipoSelecionado = watch('tipo');

  // Resetar campos de hierarquia quando o tipo mudar
  useEffect(() => {
    if (tipoSelecionado === 'supervisor' || tipoSelecionado === 'master') {
      setValue('supervisor_id', null);
    }
  }, [tipoSelecionado, setValue]);

  const validateCurrentStep = async () => {
    const values = watch();
    let isValid = true;
    clearErrors();

    switch (currentStep) {
      case 1:
        if (!values.nome || values.nome.length < 1) {
          setError('nome', { message: 'Nome é obrigatório' });
          isValid = false;
        }
        if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
          setError('email', { message: 'Email inválido' });
          isValid = false;
        }
        break;
      case 2:
        if (!values.tipo) {
          setError('tipo', { message: 'Selecione o tipo' });
          isValid = false;
        }
        break;
      case 3:
        if (isSuperAdmin && !consultor && !values.tenant_id) {
          setError('tenant_id', { message: 'Selecione uma empresa' });
          isValid = false;
        }
        break;
    }

    return isValid;
  };

  const nextStep = async (e?: React.BaseSyntheticEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const isValid = await validateCurrentStep();
    if (isValid) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = (e?: React.BaseSyntheticEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleFinalSubmit = async (data: ConsultorFormData) => {
    if (currentStep < 3) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <ConsultorFormHeader isDialog={true} isEditing={!!consultor} />

      <Card variant="borderless" styles={{ body: { padding: '32px' } }} className="rounded-t-none shadow-sm">
        <ConsultorProgressSteps activeStep={currentStep} />

        <Form layout="vertical">

          {/* Step 1: Informações Básicas */}
          {currentStep === 1 && (
            <div key="step-1" className="animate-in fade-in slide-in-from-right-4 duration-300">
              <Row gutter={16}>
                <Col xs={24} md={16}>
                  <Form.Item
                    label="Nome Completo"
                    required
                    validateStatus={errors.nome ? 'error' : ''}
                    help={errors.nome?.message}
                  >
                    <Controller
                      name="nome"
                      control={control}
                      render={({ field }) => (
                        <Input size="large" prefix={<UserOutlined className="text-gray-400" />} placeholder="Digite o nome completo" {...field} />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Código"
                    validateStatus={errors.codigo ? 'error' : ''}
                    help={errors.codigo?.message}
                  >
                    <Controller
                      name="codigo"
                      control={control}
                      render={({ field }) => (
                        <Input size="large" placeholder="Ex: 1024" {...field} />
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Email"
                    validateStatus={errors.email ? 'error' : ''}
                    help={errors.email?.message}
                  >
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <Input size="large" prefix={<MailOutlined className="text-gray-400" />} placeholder="email@exemplo.com" {...field} />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Telefone"
                    validateStatus={errors.telefone ? 'error' : ''}
                    help={errors.telefone?.message}
                  >
                    <Controller
                      name="telefone"
                      control={control}
                      render={({ field }) => (
                        <Input size="large" prefix={<PhoneOutlined className="text-gray-400" />} placeholder="(00) 00000-0000" {...field} />
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="CPF"
                    validateStatus={errors.cpf ? 'error' : ''}
                    help={errors.cpf?.message}
                  >
                    <Controller
                      name="cpf"
                      control={control}
                      render={({ field }) => (
                        <Input size="large" prefix={<IdcardOutlined className="text-gray-400" />} placeholder="000.000.000-00" {...field} />
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          )}

          {/* Step 2: Hierarquia */}
          {currentStep === 2 && (
            <div key="step-2" className="animate-in fade-in slide-in-from-right-4 duration-300">
              <Title level={4} style={{ marginBottom: 24 }}>Tipo e Hierarquia</Title>

              <Form.Item
                label="Tipo de Profissional"
                required
                validateStatus={errors.tipo ? 'error' : ''}
                help={errors.tipo?.message}
              >
                <Controller
                  name="tipo"
                  control={control}
                  render={({ field }) => (
                    <Select size="large" placeholder="Selecione o tipo" {...field}>
                      <Select.Option value="supervisor">Supervisor</Select.Option>
                      <Select.Option value="lider">Líder de Vendas</Select.Option>
                      <Select.Option value="relacionador">Relacionador</Select.Option>
                      <Select.Option value="master">Master</Select.Option>
                    </Select>
                  )}
                />
              </Form.Item>

              <div style={{ marginTop: 24 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  {tipoSelecionado === 'supervisor'
                    ? 'Supervisores gerenciam sua própria equipe.'
                    : tipoSelecionado === 'master'
                      ? 'Consultores Master atuam de forma independente.'
                      : 'Selecione o supervisor responsável por este profissional:'}
                </Text>

                {(tipoSelecionado === 'lider' || tipoSelecionado === 'relacionador') && (
                  <Form.Item
                    label="Supervisor Responsável"
                    validateStatus={errors.supervisor_id ? 'error' : ''}
                    help={errors.supervisor_id?.message}
                  >
                    <Controller
                      name="supervisor_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          size="large"
                          placeholder="Selecione o supervisor"
                          {...field}
                          onChange={(val) => field.onChange(val)}
                          value={field.value}
                        >
                          {supervisores.map((s) => (
                            <Select.Option key={s.id} value={s.id}>
                              {s.nome} {s.codigo ? `(${s.codigo})` : ''}
                            </Select.Option>
                          ))}
                        </Select>
                      )}
                    />
                  </Form.Item>
                )}

                {(tipoSelecionado === 'supervisor' || tipoSelecionado === 'master') && (
                  <Alert
                    message="Configuração de Hierarquia"
                    description={
                      tipoSelecionado === 'supervisor'
                        ? "Este profissional ocupará o topo da hierarquia de sua equipe."
                        : "Este profissional não possui supervisão direta no sistema."
                    }
                    type="info"
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Step 3: Configurações */}
          {currentStep === 3 && (
            <div key="step-3" className="animate-in fade-in slide-in-from-right-4 duration-300">
              <Title level={4} style={{ marginBottom: 24 }}>Configurações de Acesso</Title>

              {isSuperAdmin && !consultor && (
                <Form.Item
                  label="Empresa (Tenant)"
                  required
                  validateStatus={errors.tenant_id ? 'error' : ''}
                  help={errors.tenant_id?.message}
                >
                  <Controller
                    name="tenant_id"
                    control={control}
                    render={({ field }) => (
                      <Select size="large" placeholder="Selecione a empresa" {...field}>
                        {tenants.map((t) => (
                          <Select.Option key={t.id} value={t.id}>{t.nome}</Select.Option>
                        ))}
                      </Select>
                    )}
                  />
                </Form.Item>
              )}

              <Form.Item
                label="Status da Conta"
                validateStatus={errors.status ? 'error' : ''}
                help={errors.status?.message}
              >
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select size="large" {...field}>
                      <Select.Option value="ativo">Ativo</Select.Option>
                      <Select.Option value="inativo">Inativo</Select.Option>
                    </Select>
                  )}
                />
              </Form.Item>

              <Alert
                message="Importante"
                description="Certifique-se de que os dados estão corretos antes de salvar. O profissional receberá acesso de acordo com o tipo selecionado."
                type="warning"
                showIcon
                style={{ marginTop: 24 }}
              />
            </div>
          )}

        </Form>

        <Divider style={{ margin: '32px 0 24px' }} />

        <Row justify="space-between">
          <Col>
            <Button
              key="btn-prev"
              size="large"
              onClick={prevStep}
              disabled={currentStep === 1}
              htmlType="button"
            >
              Anterior
            </Button>
          </Col>
          <Col>
            <Space size={12}>
              <Button key="btn-cancel" size="large" onClick={onCancel} htmlType="button">
                Cancelar
              </Button>
              {currentStep < 3 ? (
                <Button
                  key="btn-next"
                  type="primary"
                  size="large"
                  onClick={nextStep}
                  htmlType="button"
                >
                  Próximo
                </Button>
              ) : (
                <Button
                  key="btn-submit"
                  type="primary"
                  size="large"
                  onClick={handleSubmit(handleFinalSubmit)}
                  htmlType="button"
                  loading={loading}
                  icon={<TeamOutlined />}
                >
                  {consultor ? 'Atualizar' : 'Cadastrar'} Profissional
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>
    </div>
  );
}